import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from 'react-bootstrap';
import { jewelleryHighlightTags } from './JewellerySpecFields';
import ListingImage from './ListingImage';

// Short attribute tags shown on the card, e.g. "Rose Gold", "Kundan", "Set of 4" - capped at 3
// so compact cards stay compact (user story 1.1's card design spec).
const buildTags = (item) => jewelleryHighlightTags(item).slice(0, 3);

export default function JewelleryCard({ item, wishlisted, onToggleWishlist, onContact, eager = false, browsePath, onNavigate }) {
  const [shareCopied, setShareCopied] = useState(false);
  const detailHref = `/items/${item._id}`;
  // homepage/user_experience.md #3 - carry the current filtered/sorted browse URL to the detail
  // page so "← Back to listings" (and scroll restore) returns the customer to this exact view.
  const linkState = browsePath ? { from: browsePath } : undefined;
  const detailUrl = `${window.location.origin}${process.env.PUBLIC_URL}${detailHref}`;
  const images = item.images || [];
  const storeName = item.attributes?.storeName;
  const tags = buildTags(item);

  const handleShare = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (navigator.share) {
      try {
        await navigator.share({ title: item.name, url: detailUrl });
      } catch {
        // user dismissed the native share sheet - not an error
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(detailUrl);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 1500);
    } catch {
      // clipboard unavailable - nothing more we can do here
    }
  };

  return (
    <div className="jewellery-card h-100 shadow-sm rounded bg-white">
      {/* Entire image + title area is one tap target (story 1.1: "faster browsing"), the heart
          stops propagation so it wishlists in place without navigating. */}
      <Link to={detailHref} state={linkState} onClick={onNavigate} className="jewellery-card__media d-block">
        {images[0] && (
          <ListingImage
            src={images[0]}
            alt={item.name}
            eager={eager}
            sizes="(max-width: 576px) 100vw, (max-width: 992px) 50vw, (max-width: 1200px) 33vw, 25vw"
          />
        )}
        {images.length > 1 && <span className="jewellery-card__counter">1/{images.length}</span>}
      </Link>

      <button
        type="button"
        className={`jewellery-card__heart${wishlisted ? ' is-active' : ''}`}
        aria-label={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggleWishlist(item._id);
        }}
      >
        {wishlisted ? '♥' : '♡'}
      </button>

      <div className="jewellery-card__body">
        <Link to={detailHref} state={linkState} onClick={onNavigate} className="jewellery-card__title">
          {item.name}
        </Link>
        <div className="fw-semibold">
          ₹{item.price}/{item.priceUnit}
        </div>
        <div className="text-muted small mb-1">
          {[item.category, item.location].filter(Boolean).join(' • ')}
        </div>

        {tags.length > 0 && (
          <div className="mb-2">
            {tags.map((tag) => (
              <Badge key={tag} bg="light" text="dark" className="border me-1 fw-normal">
                ✦ {tag}
              </Badge>
            ))}
          </div>
        )}

        {storeName && <div className="text-muted small mb-2">{storeName}</div>}

        <div className="jewellery-card__actions">
          <Link to={detailHref} state={linkState} onClick={onNavigate} className="btn btn-sm btn-primary">
            View Details
          </Link>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => onContact(item)}>
            Contact Owner
          </button>
          <button type="button" className="btn btn-sm btn-outline-secondary jewellery-card__share" onClick={handleShare} aria-label="Share">
            {shareCopied ? '✓' : '⤴'}
          </button>
        </div>
      </div>
    </div>
  );
}
